import {
  motion,
} from "framer-motion";

import {
  Link,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import {
  getRoutePath,
} from "../../../site/config/routes";

import sessionItems from "../../../site/data/sessions";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  SESSIONS SECTION
  ============================================================

  Kuvaukset esitellään kuvallisina editorial-kortteina.

  Desktop:
  - kolme erikokoista kuvallista korttia
  - valokuvakirjamainen rytmi

  Mobile:
  - kortit pinoutuvat
  - kuvat pysyvät suurina
*/

export default function SessionsSection({
  language = "fi",
}) {
  const {
    t,
  } = useTranslation("common");

  return (
    <section
      id="sessions"
      className="
        relative
        overflow-hidden
        border-t
        border-border
        bg-surface
      "
    >
      <div
        className="
          mx-auto
          w-full
          max-w-editorial
          px-6
          py-24

          sm:px-10
          sm:py-32

          lg:px-16
          lg:py-40
        "
      >
        {/* ====================================================
            HEADER
            ==================================================== */}

        <div
          className="
            mb-16
            grid
            gap-10

            lg:mb-24
            lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.34fr)]
            lg:items-end
            lg:gap-16
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 24,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.4,
            }}
            transition={{
              duration: 0.85,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <p
              className="
                mb-6
                text-[9px]
                font-medium
                uppercase
                tracking-[0.28em]
                text-muted

                sm:text-[10px]
              "
            >
              03 /{" "}
              {t(
                "sessionsSection.eyebrow"
              )}
            </p>

            <h2
              className="
                m-0
                max-w-4xl
                font-display
                text-[clamp(3rem,7vw,7rem)]
                font-normal
                leading-[0.9]
                tracking-[-0.05em]
                text-text
              "
            >
              {t(
                "sessionsSection.title"
              )}
            </h2>
          </motion.div>

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.5,
            }}
            transition={{
              duration: 0.8,
              delay: 0.12,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <div
              className="
                mb-7
                h-px
                w-10
                bg-text
              "
              aria-hidden="true"
            />

            <p
              className="
                m-0
                text-sm
                leading-7
                text-muted

                sm:text-base
                sm:leading-8
              "
            >
              {t(
                "sessionsSection.description"
              )}
            </p>
          </motion.div>
        </div>

        {/* ====================================================
            SESSION CARDS
            ==================================================== */}

        <div
          className="
            grid
            gap-14

            lg:grid-cols-12
            lg:items-start
            lg:gap-x-6
            lg:gap-y-20
          "
        >
          {sessionItems
            .filter(
              (item) =>
                item.featured === true
            )
            .map(
            (
              item,
              index
            ) => {
              const layoutClass =
                index === 0
                  ? "lg:col-span-7"
                  : index === 1
                    ? "lg:col-span-5 lg:mt-24"
                    : "lg:col-span-5 lg:col-start-7 lg:-mt-24";

              const aspectClass =
                index === 0
                  ? "aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5]"
                  : index === 1
                    ? "aspect-[4/5]"
                    : "aspect-[4/5]";

              return (
                <motion.article
                  key={item.id}
                  className={
                    layoutClass
                  }
                  initial={{
                    opacity: 0,
                    y: 36,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.18,
                  }}
                  transition={{
                    duration: 0.9,
                    delay:
                      index * 0.08,
                    ease: [
                      0.22,
                      1,
                      0.36,
                      1,
                    ],
                  }}
                >
                  <Link
                    to={getRoutePath(
                      "services",
                      language
                    )}
                    className="
                      group
                      block
                      text-text
                      no-underline
                    "
                  >
                    {/* IMAGE */}

                    <div
                      className={`
                        relative
                        overflow-hidden
                        bg-canvas
                        ${aspectClass}
                      `}
                    >
                      <img
                        src={
                          item.image
                        }
                        alt={t(
                          `sessionsSection.items.${item.translationKey}.alt`
                        )}
                        className="
                          h-full
                          w-full
                          object-cover
                          transition-transform
                          duration-[900ms]
                          ease-out

                          group-hover:scale-[1.025]
                        "
                      />

                      <div
                        className="
                          absolute
                          right-5
                          top-5
                          flex
                          h-11
                          w-11
                          items-center
                          justify-center
                          rounded-full
                          border
                          border-white/50
                          bg-black/10
                          text-[10px]
                          tracking-[0.16em]
                          text-white
                          backdrop-blur-sm

                          sm:right-7
                          sm:top-7
                        "
                      >
                        {item.id}
                      </div>
                    </div>

                    {/* TEXT */}

                    <div
                      className="
                        border-b
                        border-border
                        pb-7
                        pt-6

                        sm:pt-7
                      "
                    >
                      <div
                        className="
                          flex
                          items-end
                          justify-between
                          gap-6
                        "
                      >
                        <div>
                          <p
                            className="
                              mb-3
                              text-[9px]
                              font-medium
                              uppercase
                              tracking-[0.24em]
                              text-muted

                              sm:text-[10px]
                            "
                          >
                            {t(
                              `sessionsSection.items.${item.translationKey}.eyebrow`
                            )}
                          </p>

                          <h3
                            className="
                              m-0
                              font-display
                              text-[clamp(2.2rem,4vw,4.2rem)]
                              font-normal
                              leading-[0.95]
                              tracking-[-0.04em]
                            "
                          >
                            {t(
                              `sessionsSection.items.${item.translationKey}.title`
                            )}
                          </h3>
                        </div>

                        <span
                          className="
                            mb-1
                            flex
                            h-10
                            w-10
                            shrink-0
                            items-center
                            justify-center
                            rounded-full
                            border
                            border-border
                            text-sm
                            transition-all
                            duration-300

                            group-hover:bg-text
                            group-hover:text-canvas
                          "
                          aria-hidden="true"
                        >
                          →
                        </span>
                      </div>

                      <p
                        className="
                          mb-0
                          mt-5
                          max-w-lg
                          text-sm
                          leading-7
                          text-muted
                        "
                      >
                        {t(
                          `sessionsSection.items.${item.translationKey}.description`
                        )}
                      </p>
                    </div>
                  </Link>
                </motion.article>
              );
            }
          )}
        </div>

        {/* ====================================================
            FOOTER LINK
            ==================================================== */}

        <motion.div
          className="
            mt-20
            flex
            justify-end

            sm:mt-28
            lg:mt-36
          "
          initial={{
            opacity: 0,
          }}
          whileInView={{
            opacity: 1,
          }}
          viewport={{
            once: true,
            amount: 0.6,
          }}
          transition={{
            duration: 0.8,
          }}
        >
          <Link
            to={getRoutePath(
              "services",
              language
            )}
            className="
              group
              inline-flex
              items-center
              gap-4
              border-b
              border-text
              pb-2
              text-[10px]
              font-semibold
              uppercase
              tracking-[0.2em]
              text-text
              no-underline
            "
          >
            {t(
              "sessionsSection.allSessions"
            )}

            <span
              className="
                transition-transform
                duration-300
                group-hover:translate-x-1
              "
              aria-hidden="true"
            >
              →
            </span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

